import { useState } from 'react';
import { Show, Button } from 'react-admin';
import { Link, useNavigate } from 'react-router-dom';
import { Box, Typography, Paper, Chip, LinearProgress, Alert, Grid, Stepper, Step, StepLabel, IconButton, Tabs, Tab } from '@mui/material';
import type { ChipColor, ProgressColor } from '@/types/chip';
import type { TESAssignmentRecord } from '@/types/entities';
import { Close, Warning, Business, CheckCircle, Schedule, Balance, Assignment, Timeline, ArrowForward } from '@mui/icons-material';
import { PageDescription } from '@/components/help';
import { TES_ASSIGNMENT_STATUS, STATUS_COLORS, DEEMING_RISK_COLORS } from './constants';

const TESAssignmentShowContent = ({ record }: { record: TESAssignmentRecord | undefined }) => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [now] = useState(() => Date.now());
  if (!record) return null;

  const tenureDays = Math.floor((now - new Date(record.assignmentPeriod.startDate).getTime()) / (1000 * 60 * 60 * 24));
    const daysUntilDeeming = 90 - tenureDays;
    const deemingPct = Math.min(100, Math.round((tenureDays / 90) * 100));
    const isDeemed = daysUntilDeeming <= 0;

    const getRiskColor = () => {
      if (isDeemed) return 'error';
      if (daysUntilDeeming <= 30) return 'warning';
      if (daysUntilDeeming <= 60) return 'info';
      return 'success';
    };

    return (
      <Box>
        <Box sx={{ background: 'linear-gradient(135deg, #e65100 0%, #ef6c00 100%)', color: 'white', p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>TES Assignment</Typography>
              <Typography variant="body2" sx={{ opacity: 0.85 }}>
                {record.employee?.id} — {record.vendor?.id}
              </Typography>
            </Box>
            <IconButton onClick={() => navigate('/tes-assignments')} sx={{ color: 'white' }}>
              <Close />
            </IconButton>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Chip label={record.status} sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }} />
            <Chip label={`Tenure: ${tenureDays} days`} sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
            <Chip label={`Role: ${record.assignmentDetails.role}`} sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
          </Box>
        </Box>

        <PageDescription entity="TES Assignment" description="A temporary employment assignment linking a worker to a client organisation." icon={<Assignment />} />

        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Schedule fontSize="small" /> Deeming Countdown — 90 Day Threshold
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary">Elapsed: {tenureDays} days</Typography>
                <Typography variant="caption">Threshold: 90 days</Typography>
              </Box>
              <LinearProgress variant="determinate" value={deemingPct} color={getRiskColor() as ProgressColor} sx={{ height: 14, borderRadius: 1 }} />
            </Box>
            <Box sx={{ textAlign: 'center', minWidth: 80 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: `${getRiskColor()}.main` }}>
                {isDeemed ? 'DEEMED' : `${daysUntilDeeming}d`}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {isDeemed ? 'Employment relationship' : 'Remaining'}
              </Typography>
            </Box>
          </Box>
          {isDeemed && (
            <Alert severity="error" sx={{ mt: 2 }} icon={<Warning />}>
              Assignment has exceeded the 90-day deeming threshold — employment relationship may be deemed with client
            </Alert>
          )}
        </Paper>

        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 3 }}>
          <Tab label="Details" icon={<Assignment />} iconPosition="start" />
          <Tab label="Timeline" icon={<Timeline />} iconPosition="start" />
          <Tab label="Liability" icon={<Balance />} iconPosition="start" />
        </Tabs>

        {tabValue === 0 && (
          <>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {[
                { label: 'Employee', value: record.employee?.id, bold: true },
                { label: 'Vendor', value: record.vendor?.id },
                { label: 'Role', value: record.assignmentDetails.role },
                { label: 'Status', value: record.status, chip: true },
                { label: 'Start Date', value: record.assignmentPeriod.startDate },
                { label: 'End Date', value: record.assignmentPeriod.endDate || '—' },
                { label: 'Tenure', value: `${tenureDays} days` },
                { label: 'Deeming Risk', value: record.deemingRisk.assessedRiskLevel, chip: true },
              ].map(f => (
                <Grid size={{ xs: 12, sm: 6, md: 3 }} key={f.label}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="caption" color="text.secondary">{f.label}</Typography>
                    {f.chip ? (
                      <Box sx={{ mt: 0.5 }}>
                        <Chip label={String(f.value)} size="small" sx={{
                          bgcolor: f.label === 'Deeming Risk'
                            ? DEEMING_RISK_COLORS[f.value as string]
                            : STATUS_COLORS[record.status],
                          color: 'white'
                        }} />
                      </Box>
                    ) : (
                      <Typography variant="body1" sx={{ fontWeight: f.bold ? 700 : 400, mt: 0.5 }}>{f.value ?? '—'}</Typography>
                    )}
                  </Paper>
                </Grid>
              ))}
            </Grid>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2 }}>Assignment Process</Typography>
                  <Stepper activeStep={TES_ASSIGNMENT_STATUS.indexOf(record.status || 'Pending')} alternativeLabel>
                    <Step completed={TES_ASSIGNMENT_STATUS.indexOf(record.status || 'Pending') > 0}>
                      <StepLabel>Pending</StepLabel>
                    </Step>
                    <Step completed={TES_ASSIGNMENT_STATUS.indexOf(record.status || 'Pending') > 1}>
                      <StepLabel>Active</StepLabel>
                    </Step>
                    <Step completed={TES_ASSIGNMENT_STATUS.indexOf(record.status || 'Pending') > 2}>
                      <StepLabel>Suspended</StepLabel>
                    </Step>
                    <Step completed={TES_ASSIGNMENT_STATUS.indexOf(record.status || 'Pending') > 3}>
                      <StepLabel>Completed</StepLabel>
                    </Step>
                  </Stepper>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2 }}>Quick Actions</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Button component={Link} to="/deeming-alerts" variant="outlined" size="small" startIcon={<Warning />}>
                      View Deeming Alerts
                    </Button>
                    <Button component={Link} to="/tesVendors" variant="outlined" size="small" startIcon={<Business />}>
                      View Vendor
                    </Button>
                    <Button component={Link} to="/liability-records" variant="outlined" size="small" startIcon={<Balance />}>
                      View Liability
                    </Button>
                    <Button component={Link} to="/employees" variant="outlined" size="small">
                      View Employee
                    </Button>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </>
        )}

        {tabValue === 1 && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Assignment Timeline</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {[
                { date: record.assignmentPeriod.startDate, label: 'Assignment Started', color: 'success.main' },
              ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).map((event, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: event.color, mt: 0.5 }} />
                  <Box>
                    <Typography variant="body2" fontWeight={600}>{event.label}</Typography>
                    <Typography variant="caption" color="text.secondary">{event.date}</Typography>
                  </Box>
                </Box>
              ))}
              {record.assignmentPeriod.endDate && (
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'grey.400', mt: 0.5 }} />
                  <Box>
                    <Typography variant="body2" fontWeight={600}>Assignment End</Typography>
                    <Typography variant="caption" color="text.secondary">{record.assignmentPeriod.endDate}</Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Paper>
        )}

        {tabValue === 2 && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Liability Overview</Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Paper sx={{ p: 2, borderLeft: '4px solid', borderColor: isDeemed ? 'error.main' : 'success.main' }}>
                  <Typography variant="caption" color="text.secondary">Deeming Status</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5 }}>
                    {isDeemed ? 'Deemed Employment Relationship' : `${daysUntilDeeming} days until deeming threshold`}
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Paper sx={{ p: 2, borderLeft: '4px solid', borderColor: 'info.main' }}>
                  <Typography variant="caption" color="text.secondary">Assignment Duration</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5 }}>{tenureDays} days</Typography>
                </Paper>
              </Grid>
            </Grid>
            <Box sx={{ mt: 2 }}>
              <Button component={Link} to="/liability-records" variant="outlined" size="small" startIcon={<Balance />}>
                View Full Liability Records
              </Button>
            </Box>
          </Paper>
        )}
      </Box>
    );
};

export const TESAssignmentShow = () => (
  <Show render={(p) => <TESAssignmentShowContent {...p} />} />
);
